import { action, makeObservable, observable, runInAction } from 'mobx';
import { Country } from 'react-phone-number-input';

import { BackendPublicEnums } from 'public-api/codegen';
import { ApiClient as PublicApiClient } from 'public-api/codegen/api';
import { signupLanguage } from 'ui-brand/languages';

import getFormErrors from './formErrors';

const PLATFORM_NAMES: Partial<Record<BackendPublicEnums.PlatformIntegrationDataTypeEnum, string>> = {
  SHOPTET_API: 'Shoptet',
  SHOPIFY_API: 'Shopify',
  PRESTASHOP_16: 'PrestaShop',
  SHOPWARE_5: 'Shopware',
  SHOPWARE_6: 'Shopware',
  SHOPRENTER_API: 'Shoprenter',
  UPGATES_API: 'Upgates',
  UNAS_API: 'UNAS',
  WEBAREAL_API: 'Webareal',
  ESHOP_RYCHLE_API: 'Eshop-rychle',
};

type AccountField = 'company_name' | 'full_name' | 'email' | 'password' | 'phone_number';

class PlatformIntegrationStore {
  platformIntegrationId: UUIDField;
  @observable platformIntegration: BackendPublic.PlatformIntegrationData | null = null;
  @observable loadFailed = false;
  @observable data = {
    company_name: '',
    full_name: '',
    email: '',
    password: '',
    phone_number: '',
    selected_products: [] as BackendPublicEnums.SelectedProductsEnum[],
  };
  @observable phoneCountry: Country | undefined = signupLanguage().country;
  @observable errors: Record<string, string> = {};

  private api: PublicApiClient;

  constructor(api: PublicApiClient, platformIntegrationId: UUIDField) {
    makeObservable(this);
    this.api = api;
    this.platformIntegrationId = platformIntegrationId;
  }

  @action.bound async load() {
    this.loadFailed = false;
    try {
      const platformIntegration = await this.api.OnboardingPlatformIntegrationApi.retrieve({
        id: this.platformIntegrationId,
      });
      runInAction(() => {
        this.platformIntegration = platformIntegration;
        this.data.company_name = platformIntegration.prefill.shop_name;
        this.data.full_name = platformIntegration.prefill.full_name;
        this.data.email = platformIntegration.prefill.email;
      });
    } catch (error) {
      runInAction(() => {
        this.loadFailed = true;
      });
    }
  }

  @action.bound updateData(data: Partial<typeof this.data>) {
    this.data = { ...this.data, ...data };
    Object.keys(data).forEach((field) => {
      delete this.errors[field];
    });
  }

  @action.bound setPhoneCountry(country?: Country) {
    this.phoneCountry = country;
  }

  @action.bound async submit() {
    const request = {
      password: this.data.password,
      selected_products: this.data.selected_products,
      ...Object.fromEntries(this.fields.map((field) => [field, this.data[field]])),
    };
    try {
      await this.api.OnboardingPlatformIntegrateApi.create({ id: this.platformIntegrationId, request });
      window.location.assign('/');
    } catch (error) {
      this.errors = await getFormErrors(error);
    }
  }

  get fields() {
    return this.platformIntegration!.account_fields as AccountField[];
  }

  get platformName() {
    return PLATFORM_NAMES[this.platformIntegration!.type] || this.platformIntegration!.type;
  }
}

export default PlatformIntegrationStore;
